import { seed } from '../data/db';
import type { ActionLevel, AgentId, Processing } from '../types/anna';
import type { AgentTask, LogEntry, Meta, SlackMessage, Tables } from '../types/db';

/**
 * Fake in-memory database. Seeded from data/db, mutated through the methods
 * below, observed through hooks/useDb. Every action Anna takes writes a log row.
 */
let state: Tables = structuredClone(seed);
const listeners = new Set<() => void>();
let logSeq = 100;

function commit(next: Tables) {
  state = next;
  listeners.forEach((fn) => fn());
}

function entry(e: {
  agent: AgentId | 'systeem';
  summary: string;
  level: ActionLevel;
  where: Processing;
  outcome: LogEntry['outcome'];
  traceId?: string;
}): LogEntry {
  return { ...e, id: `l-${logSeq++}`, time: state.meta.time };
}

export const db = {
  getState: (): Tables => state,

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },

  setMeta(patch: Partial<Meta>) {
    commit({ ...state, meta: { ...state.meta, ...patch } });
  },

  log(e: Parameters<typeof entry>[0]) {
    commit({ ...state, log: [...state.log, entry(e)] });
  },

  approveProposal(id: string) {
    const p = state.proposals.find((x) => x.id === id);
    if (!p || p.status !== 'wacht') return;
    commit({
      ...state,
      proposals: state.proposals.map((x) => (x.id === id ? { ...x, status: 'goedgekeurd' } : x)),
      pullRequests:
        p.kind === 'code'
          ? state.pullRequests.map((pr) => (p.refIds.includes(pr.id) ? { ...pr, status: 'goedgekeurd' } : pr))
          : state.pullRequests,
      agentTasks:
        p.kind === 'boekhouding'
          ? state.agentTasks.map((t) => (t.agent === 'boekhouding' ? { ...t, status: 'klaar', stepLabel: 'ingeboekt' } : t))
          : state.agentTasks,
      log: [
        ...state.log,
        entry({ agent: p.agent, summary: `${p.title} · goedgekeurd`, level: 'naOk', where: p.where, outcome: 'goedgekeurd', traceId: p.traceId }),
      ],
    });
  },

  rejectProposal(id: string) {
    const p = state.proposals.find((x) => x.id === id);
    if (!p || p.status !== 'wacht') return;
    commit({
      ...state,
      proposals: state.proposals.map((x) => (x.id === id ? { ...x, status: 'afgewezen' } : x)),
      log: [
        ...state.log,
        entry({ agent: p.agent, summary: `${p.title} · afgewezen`, level: 'naOk', where: p.where, outcome: 'afgewezen', traceId: p.traceId }),
      ],
    });
  },

  /** Demo helper: put the approval queue back to its seed state. */
  resetProposals() {
    commit({
      ...state,
      proposals: structuredClone(seed.proposals),
      pullRequests: structuredClone(seed.pullRequests),
      agentTasks: structuredClone(seed.agentTasks),
    });
  },

  setLight(roomId: string, value: number) {
    const room = state.rooms.find((r) => r.id === roomId);
    if (!room) return;
    commit({
      ...state,
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, light: value } : r)),
      log: [
        ...state.log,
        entry({ agent: 'huis', summary: `Licht ${room.name.toLowerCase()} op ${value}%`, level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd', traceId: 'licht' }),
      ],
    });
  },

  setTarget(roomId: string, value: number, level: ActionLevel = 'zelf') {
    const room = state.rooms.find((r) => r.id === roomId);
    if (!room) return;
    commit({
      ...state,
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, target: value } : r)),
      log: [
        ...state.log,
        entry({
          agent: 'huis',
          summary: `Verwarming ${room.name.toLowerCase()} naar ${value} °C`,
          level,
          where: 'lokaal',
          outcome: 'uitgevoerd',
          traceId: level === 'tenzijBezwaar' ? 'verwarming' : 'klimaat',
        }),
      ],
    });
  },

  updateTask(id: string, patch: Partial<AgentTask>) {
    commit({ ...state, agentTasks: state.agentTasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) });
  },

  /** Stream the next scripted event of a task into its activity trace. */
  advanceTask(id: string) {
    const t = state.agentTasks.find((x) => x.id === id);
    if (!t || t.status !== 'bezig' || !t.upcoming?.length) return;
    const [next, ...rest] = t.upcoming;
    const step = next.step ?? t.step;
    const done = rest.length === 0 && step >= t.totalSteps;
    commit({
      ...state,
      agentTasks: state.agentTasks.map((x) =>
        x.id === id
          ? {
              ...x,
              upcoming: rest,
              events: [...x.events, next.event],
              step,
              stepLabel: done ? 'klaar' : next.stepLabel ?? x.stepLabel,
              status: done ? 'klaar' : x.status,
            }
          : x,
      ),
      log: done
        ? [...state.log, entry({ agent: t.agent, summary: `${t.title} · klaar`, level: 'zelf', where: t.where, outcome: 'uitgevoerd' })]
        : state.log,
    });
  },

  setGpu(gpu: Meta['gpu']) {
    commit({ ...state, meta: { ...state.meta, gpu } });
  },

  finishRender(id: string) {
    const r = state.renders.find((x) => x.id === id);
    if (!r || r.status === 'klaar') return;
    commit({
      ...state,
      meta: { ...state.meta, gpu: 'actief' },
      renders: state.renders.map((x) => (x.id === id ? { ...x, status: 'klaar' } : x)),
      agentTasks: state.agentTasks.map((t) =>
        t.agent === 'studio'
          ? { ...t, status: 'klaar', step: t.totalSteps, stepLabel: 'klaar', events: [...t.events, 'render klaar'] }
          : t,
      ),
      log: [...state.log, entry({ agent: 'studio', summary: `${r.title} · klaar`, level: 'zelf', where: r.where, outcome: 'uitgevoerd', traceId: 'render-landing' })],
    });
  },

  setSlack(id: string, status: SlackMessage['status']) {
    const m = state.slackMessages.find((x) => x.id === id);
    if (!m) return;
    commit({
      ...state,
      slackMessages: state.slackMessages.map((x) => (x.id === id ? { ...x, status } : x)),
      agentTasks:
        status === 'concept'
          ? state.agentTasks.map((t) =>
              t.agent === 'slack' ? { ...t, stepLabel: '1 vraag aan jou · concept klaar', events: [...t.events, 'concept-antwoord klaar'] } : t,
            )
          : state.agentTasks,
      log:
        status === 'geplaatst'
          ? [...state.log, entry({ agent: 'slack', summary: `Antwoord geplaatst in ${m.channel}`, level: 'naOk', where: 'lokaal', outcome: 'goedgekeurd', traceId: 'slack-bakker' })]
          : state.log,
    });
  },

  slackQuestionArrived(id: string) {
    const m = state.slackMessages.find((x) => x.id === id);
    if (!m) return;
    commit({
      ...state,
      agentTasks: state.agentTasks.map((t) =>
        t.agent === 'slack' ? { ...t, step: 2, stepLabel: '1 vraag aan jou gesignaleerd', events: [...t.events, '1 vraag aan jou gesignaleerd'] } : t,
      ),
    });
  },

  addCloudCost(euro: number) {
    if (state.meta.cloud.localOnly) return;
    commit({ ...state, meta: { ...state.meta, cloud: { ...state.meta.cloud, spent: state.meta.cloud.spent + euro } } });
  },

  /** Over budget: every running task moves to a local model for the rest of the day. */
  switchToLocal(spent: number) {
    commit({
      ...state,
      meta: { ...state.meta, cloud: { ...state.meta.cloud, spent, localOnly: true } },
      agentTasks: state.agentTasks.map((t) => (t.where === 'cloud' ? { ...t, where: 'lokaal', model: 'Qwen 32B', events: [...t.events, 'overgeschakeld op lokaal model'] } : t)),
      log: [...state.log, entry({ agent: 'systeem', summary: 'Cloudbudget 80%: rest van de dag lokaal', level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd', traceId: 'cloud-budget' })],
    });
  },

  setDevice(id: string, status: string) {
    commit({ ...state, devices: state.devices.map((d) => (d.id === id ? { ...d, status } : d)) });
  },

  reset() {
    commit(structuredClone(seed));
  },
};
